/**
 * @fileoverview "Compartilhar quadro": os links externos (sem login, só
 * leitura) do quadro aberto no CRM. Quem abre precisa de crm.boards.share (na
 * regra de hoje, admin ou super; a API confere de novo).
 *
 * Telas dentro do modal: a lista dos links do quadro, o link novo, o link
 * criado (o único momento em que a senha aparece), a senha e a validade de um
 * link. O endereço do link só vem do servidor na criação, ao copiar de novo e
 * ao gerar um novo; a senha nunca volta.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import { X, Share2, Plus, Copy, Check, CircleCheck, Lock, LockOpen, CalendarClock, Infinity as InfinityIcon } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../ConfirmationModal";
import { i18n } from "../../translate/i18n";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { formatDateTime } from "../../utils/formatLocaleDate";
import { useCopyFeedback } from "../../hooks/useCopyFeedback";
import ShareForm, { EMPTY_FORM } from "./ShareForm";
import ShareRow from "./ShareRow";
import ExpirationPicker, { DEFAULT_EXPIRATION } from "./ExpirationPicker";
import PasswordInput from "./PasswordInput";
import { passwordProblem, resolveExpiration, toLocalInput } from "./shareHelpers";
import useStyles from "./styles";

const t = (key, options) => i18n.t(`boardShare.${key}`, options);

const STATE_ORDER = { active: 0, expired: 1, revoked: 2 };
const sortShares = (list) =>
  [...list].sort((a, b) => STATE_ORDER[a.state] - STATE_ORDER[b.state] || new Date(b.createdAt) - new Date(a.createdAt));

const replaceShare = (list, share) => sortShares(list.map((item) => (item.id === share.id ? share : item)));

/** O link criado (ou trocado): o endereço para copiar e, se tiver, a senha escolhida. */
const CreatedLink = ({ created }) => {
  const classes = useStyles();
  const { copied, copy } = useCopyFeedback(created.url, {
    copiedMessage: t("created.copied"),
    failedMessage: t("created.copyFailed"),
  });
  const { share } = created;
  // Link novo, link trocado ou só o endereço de um que já existe (sem área de transferência).
  const variant = created.copyOnly ? "copyOnly" : created.renewed ? "renewed" : "new";
  return (
    <>
      <div className={classes.success}>
        <span className={classes.successIcon}>
          <CircleCheck />
        </span>
        <div>
          <p className={classes.title} style={{ fontSize: 15 }}>
            {t(`created.${variant}Title`)}
          </p>
          <p className={classes.text}>{t(`created.${variant}Text`)}</p>
        </div>
      </div>
      <div className={classes.section}>
        <p className={classes.label}>{t("created.link")}</p>
        <div className={classes.linkBox}>
          <input
            className={classes.linkText}
            value={created.url}
            readOnly
            onFocus={(e) => e.target.select()}
            aria-label={t("created.link")}
          />
          <button type="button" className={`${classes.btnGhost} ${classes.btnSmall} ${copied ? classes.copied : ""}`} onClick={copy}>
            {copied ? <Check /> : <Copy />}
            {copied ? t("created.copiedShort") : t("created.copyLink")}
          </button>
        </div>
        <div className={classes.facts}>
          <span className={classes.fact}>
            {share.passwordProtected ? <Lock /> : <LockOpen />}
            {share.passwordProtected ? t("row.withPassword") : t("row.withoutPassword")}
          </span>
          <span className={classes.fact}>
            {share.expiresAt ? <CalendarClock /> : <InfinityIcon />}
            {share.expiresAt ? t("row.until", { date: formatDateTime(share.expiresAt) }) : t("row.permanent")}
          </span>
        </div>
      </div>
      {created.password && (
        <div className={classes.section}>
          <PasswordInput value={created.password} onChange={() => undefined} readOnly />
          <p className={classes.hint}>{t("created.passwordNote")}</p>
        </div>
      )}
    </>
  );
};

const BoardShareModal = ({ open, board, onClose }) => {
  const classes = useStyles();
  const [view, setView] = useState("list");
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [target, setTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [expiration, setExpiration] = useState(DEFAULT_EXPIRATION);
  const [confirm, setConfirm] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const loadSeq = useRef(0);
  const copiedTimer = useRef(null);

  const boardId = board?.id;
  const base = boardId ? `/crm-boards/${boardId}/shares` : null;

  const load = useCallback(async () => {
    if (!base) return [];
    const seq = ++loadSeq.current;
    setLoading(true);
    setLoadError(false);
    try {
      const { data } = await api.get(base);
      if (seq !== loadSeq.current) return null;
      const list = sortShares(Array.isArray(data) ? data : []);
      setShares(list);
      return list;
    } catch (err) {
      if (seq === loadSeq.current) setLoadError(true);
      return null;
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [base]);

  // Ao abrir (ou trocar de quadro): lista; sem link nenhum, já o formulário.
  useEffect(() => {
    if (!open || !base) return;
    setShares([]);
    setCreated(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setView("list");
    load().then((list) => {
      if (list && list.length === 0) setView("form");
    });
  }, [open, base, load]);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  // Fechou: some com a senha e o endereço da memória do modal.
  const handleClose = () => {
    if (saving) return;
    setCreated(null);
    setNewPassword("");
    setForm(EMPTY_FORM);
    onClose();
  };

  // Volta à lista, relida (o último acesso de cada link muda enquanto o modal está aberto).
  const backToList = () => {
    setCreated(null);
    setTarget(null);
    setNewPassword("");
    setPasswordError("");
    setView(shares.length ? "list" : "form");
    load();
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = t("form.nameRequired");
    if (form.usePassword) {
      const problem = passwordProblem(form.password);
      if (problem) errors.password = t(`form.${problem}`);
    }
    const { invalid } = resolveExpiration(form.expiration);
    if (invalid) errors.expiration = t("form.customInvalid");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreate = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const { expiresAt } = resolveExpiration(form.expiration);
      const { data } = await api.post(base, {
        name: form.name.trim(),
        expiresAt,
        password: form.usePassword ? form.password : null,
        showContactInfo: form.showContactInfo,
        showCardDetails: form.showCardDetails,
      });
      setShares((list) => sortShares([data.share, ...list]));
      setCreated({ share: data.share, url: data.url, password: form.usePassword ? form.password : null });
      setForm(EMPTY_FORM);
      setView("created");
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  // O endereço vem do servidor na hora do clique (não fica na lista): por isso
  // não dá para usar o useCopyFeedback, que copia um texto que já se tem.
  const handleCopy = async (share) => {
    setBusyId(share.id);
    try {
      const { data } = await api.post(`${base}/${share.id}/link`);
      if (await copyToClipboard(data.url)) {
        toast.success(t("created.copied"));
        setCopiedId(share.id);
        clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopiedId(null), 1600);
      } else {
        // Sem área de transferência: o endereço na tela, para copiar à mão.
        setCreated({ share, url: data.url, password: null, copyOnly: true });
        setView("created");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const openPassword = (share) => {
    setTarget(share);
    setNewPassword("");
    setPasswordError("");
    setView("password");
  };

  // Abre na validade de agora: salvar sem mexer não muda nada.
  const openValidity = (share) => {
    setTarget(share);
    setExpiration(
      share.expiresAt
        ? { ...DEFAULT_EXPIRATION, kind: "temporary", preset: "custom", custom: toLocalInput(new Date(share.expiresAt)) }
        : { ...DEFAULT_EXPIRATION, kind: "permanent" }
    );
    setView("validity");
  };

  const savePassword = async (password) => {
    if (password !== null) {
      const problem = passwordProblem(password);
      if (problem) {
        setPasswordError(t(`form.${problem}`));
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.put(`${base}/${target.id}/password`, { password });
      setShares((list) => replaceShare(list, data));
      toast.success(password === null ? t("password.removed") : t("password.saved"));
      backToList();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const saveValidity = async () => {
    const { expiresAt, invalid } = resolveExpiration(expiration);
    if (invalid) return;
    setSaving(true);
    try {
      const { data } = await api.put(`${base}/${target.id}`, { expiresAt });
      setShares((list) => replaceShare(list, data));
      toast.success(t("validity.saved"));
      backToList();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const runConfirmed = async () => {
    const { kind, share } = confirm;
    setSaving(true);
    try {
      if (kind === "revoke") {
        const { data } = await api.post(`${base}/${share.id}/revoke`);
        setShares((list) => replaceShare(list, data));
        toast.success(t("revoke.done"));
      } else if (kind === "renew") {
        const { data } = await api.post(`${base}/${share.id}/renew`);
        setShares((list) => replaceShare(list, data.share));
        setCreated({ share: data.share, url: data.url, password: null, renewed: true });
        setView("created");
      } else if (kind === "removePassword") {
        setSaving(false);
        setConfirm(null);
        await savePassword(null);
        return;
      }
      setConfirm(null);
    } catch (err) {
      toastError(err);
      setConfirm(null);
    } finally {
      setSaving(false);
    }
  };

  // Embaixo do título: o quadro (ou o link que está sendo mudado).
  const heading = useMemo(() => {
    if (view === "form") return { title: t("newLink"), subtitle: board?.name };
    if (view === "password") return { title: t("password.title"), subtitle: target?.name };
    if (view === "validity") return { title: t("validity.title"), subtitle: target?.name };
    return { title: t("title"), subtitle: board?.name };
  }, [view, target, board]);

  if (!board) return null;

  const confirmTexts = confirm ? t(`${confirm.kind}`, { returnObjects: true }) : null;

  return (
    <Dialog open={open} onClose={handleClose} classes={{ paper: classes.paper }} maxWidth={false} aria-labelledby="board-share-title">
      <div className={classes.header}>
        <span className={classes.headIcon}>
          <Share2 />
        </span>
        <div className={classes.headText}>
          <h2 id="board-share-title" className={classes.title}>
            {heading.title}
          </h2>
          <p className={`${classes.subtitle} ${classes.boardName}`}>{heading.subtitle}</p>
        </div>
        <button type="button" className={`${classes.iconBtn} ${classes.close}`} onClick={handleClose} aria-label={t("close")} disabled={saving}>
          <X />
        </button>
      </div>

      <div className={classes.body}>
        {(view === "list" || view === "form") && <p className={classes.text}>{t("description")}</p>}

        {view === "list" && (
          <div className={classes.section}>
            <div className={classes.sectionHead}>
              <p className={classes.label}>{t("listTitle", { count: shares.length })}</p>
            </div>
            {loading && !shares.length ? (
              <div className={classes.loading}>
                <CircularProgress size={22} color="inherit" />
              </div>
            ) : loadError ? (
              <div className={classes.section} style={{ alignItems: "center" }}>
                <p className={classes.empty}>{t("loadError")}</p>
                <button type="button" className={classes.btnGhost} onClick={load}>
                  {t("retry")}
                </button>
              </div>
            ) : shares.length ? (
              <div className={classes.list}>
                {shares.map((share) => (
                  <ShareRow
                    key={share.id}
                    share={share}
                    busy={busyId === share.id}
                    copied={copiedId === share.id}
                    onCopy={handleCopy}
                    onPassword={openPassword}
                    onValidity={openValidity}
                    onRenew={(s) => setConfirm({ kind: "renew", share: s })}
                    onRevoke={(s) => setConfirm({ kind: "revoke", share: s })}
                  />
                ))}
              </div>
            ) : (
              <p className={classes.empty}>{t("empty")}</p>
            )}
          </div>
        )}

        {view === "form" && <ShareForm value={form} onChange={setForm} errors={formErrors} disabled={saving} />}

        {view === "created" && created && <CreatedLink created={created} />}

        {view === "password" && target && (
          <>
            <p className={classes.text}>{t("password.text")}</p>
            <PasswordInput
              value={newPassword}
              onChange={(value) => {
                setNewPassword(value);
                setPasswordError("");
              }}
              error={passwordError}
              autoFocus
              disabled={saving}
            />
          </>
        )}

        {view === "validity" && target && (
          <>
            <p className={classes.text}>
              {target.expiresAt ? t("validity.current", { date: formatDateTime(target.expiresAt) }) : t("validity.currentPermanent")}{" "}
              {t("validity.text")}
            </p>
            <ExpirationPicker value={expiration} onChange={setExpiration} disabled={saving} />
          </>
        )}
      </div>

      <div className={classes.footer}>
        {view === "list" && (
          <>
            <button type="button" className={classes.btnGhost} onClick={handleClose}>
              {t("close")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={() => setView("form")} disabled={loading && !shares.length}>
              <Plus />
              {t("newLink")}
            </button>
          </>
        )}
        {view === "form" && (
          <>
            <button type="button" className={classes.btnGhost} onClick={shares.length ? backToList : handleClose} disabled={saving}>
              {shares.length ? t("back") : t("cancel")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={handleCreate} disabled={saving}>
              {saving ? <CircularProgress size={16} color="inherit" /> : null}
              {saving ? t("form.creating") : t("form.create")}
            </button>
          </>
        )}
        {view === "created" && (
          <>
            <button type="button" className={classes.btnGhost} onClick={backToList}>
              {t("created.seeLinks")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={handleClose}>
              {t("created.done")}
            </button>
          </>
        )}
        {view === "password" && target && (
          <>
            {target.passwordProtected && (
              <button type="button" className={classes.btnDanger} onClick={() => setConfirm({ kind: "removePassword", share: target })} disabled={saving}>
                {t("password.remove")}
              </button>
            )}
            <span className={classes.footerSpacer} />
            <button type="button" className={classes.btnGhost} onClick={backToList} disabled={saving}>
              {t("cancel")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={() => savePassword(newPassword)} disabled={saving}>
              {t("password.save")}
            </button>
          </>
        )}
        {view === "validity" && target && (
          <>
            <button type="button" className={classes.btnGhost} onClick={backToList} disabled={saving}>
              {t("cancel")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={saveValidity} disabled={saving || resolveExpiration(expiration).invalid}>
              {t("validity.save")}
            </button>
          </>
        )}
      </div>

      <ConfirmationModal
        title={confirmTexts?.title || ""}
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirmed}
        confirmLoading={saving}
        confirmLabel={confirmTexts?.confirm}
      >
        {confirmTexts?.text}
      </ConfirmationModal>
    </Dialog>
  );
};

export default BoardShareModal;
